/**
 * useGuardian Hook
 *
 * Manages guardian enrollment state and GraphQL operations.
 *
 * FERP-PII-052: Guardian enrollment for L1G field access.
 *
 * @example
 * ```tsx
 * const {
 *   myGuardians,
 *   guardedUsers,
 *   enrollGuardian,
 *   submitForReview,
 *   revokeGuardian
 * } = useGuardian();
 * ```
 */

import { useState, useCallback } from 'react';
// eslint-disable-next-line no-restricted-imports -- Data-layer hook, not UI component
import { useQuery, useMutation } from '@apollo/client/react';
import { gql } from '@apollo/client';
import { useAuth } from '../../auth/AuthContext';

// ============================================================================
// GraphQL Operations
// ============================================================================

const MY_GUARDIANS = gql`
  query MyGuardians {
    myGuardians {
      guardianId
      userId
      guardianUserId
      guardianType
      status
      guardianName
      relationship
      guardianPhone
      guardianEmail
      documents {
        documentType
        filename
        mimeType
        sizeBytes
        uploadedAt
        verified
        verifiedAt
        expiresAt
      }
      createdAt
      updatedAt
      verifiedAt
      activatedAt
      lastAccessAt
      authorityExpiresAt
      canAccess
      accessibleFields
    }
  }
`;

const GUARDED_USERS = gql`
  query GuardedUsers {
    guardedUsers {
      guardianId
      userId
      guardianUserId
      guardianType
      status
      guardianName
      relationship
      canAccess
      accessibleFields
    }
  }
`;

const CAN_ACCESS_L1G = gql`
  query CanAccessL1g($userId: String!) {
    canAccessL1g(userId: $userId) {
      canAccess
      guardian {
        guardianId
        guardianType
        status
      }
      accessibleFields
    }
  }
`;

const PENDING_ENROLLMENTS = gql`
  query PendingEnrollments {
    pendingEnrollments {
      guardianId
      userId
      guardianUserId
      guardianType
      status
      guardianName
      relationship
      documents {
        documentType
        verified
      }
      createdAt
    }
  }
`;

const ENROLL_GUARDIAN = gql`
  mutation EnrollGuardian($input: EnrollGuardianInputGql!) {
    enrollGuardian(input: $input) {
      success
      guardian {
        guardianId
        status
        guardianType
      }
      error
    }
  }
`;

const SUBMIT_FOR_REVIEW = gql`
  mutation SubmitForReview($guardianId: String!, $userId: String!) {
    submitForReview(guardianId: $guardianId, userId: $userId) {
      success
      guardian {
        guardianId
        status
      }
      error
    }
  }
`;

const VERIFY_GUARDIAN = gql`
  mutation VerifyGuardian($input: VerifyGuardianInputGql!) {
    verifyGuardian(input: $input) {
      success
      guardian {
        guardianId
        status
      }
      approved
      error
    }
  }
`;

const SUSPEND_GUARDIAN = gql`
  mutation SuspendGuardian($guardianId: String!, $userId: String!, $reason: String!) {
    suspendGuardian(guardianId: $guardianId, userId: $userId, reason: $reason) {
      success
      guardian {
        guardianId
        status
      }
      error
    }
  }
`;

const REVOKE_GUARDIAN = gql`
  mutation RevokeGuardian($guardianId: String!, $userId: String!) {
    revokeGuardian(guardianId: $guardianId, userId: $userId)
  }
`;

// ============================================================================
// Types
// ============================================================================

export enum GuardianType {
  HEALTHCARE_PROXY = 'HealthcareProxy',
  POWER_OF_ATTORNEY = 'PowerOfAttorney',
  COURT_APPOINTED = 'CourtAppointed',
  EMERGENCY_CONTACT = 'EmergencyContact',
}

export enum GuardianStatus {
  PENDING_VERIFICATION = 'PendingVerification',
  UNDER_REVIEW = 'UnderReview',
  ACTIVE = 'Active',
  SUSPENDED = 'Suspended',
  REVOKED = 'Revoked',
  EXPIRED = 'Expired',
}

export interface GuardianDocument {
  documentType: string;
  filename: string;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  verified: boolean;
  verifiedAt?: string;
  expiresAt?: string;
}

export interface Guardian {
  guardianId: string;
  userId: string;
  guardianUserId: string;
  guardianType: GuardianType;
  status: GuardianStatus;
  guardianName: string;
  relationship: string;
  guardianPhone?: string;
  guardianEmail?: string;
  documents: GuardianDocument[];
  createdAt: string;
  updatedAt: string;
  verifiedAt?: string;
  activatedAt?: string;
  lastAccessAt?: string;
  authorityExpiresAt?: string;
  canAccess: boolean;
  accessibleFields: string[];
}

export interface EnrollGuardianInput {
  userId: string;
  guardianUserId: string;
  guardianType: GuardianType;
  guardianName: string;
  relationship: string;
  guardianPhone?: string;
  guardianEmail?: string;
  authorityExpiresAt?: string;
}

export interface VerifyGuardianInput {
  guardianId: string;
  userId: string;
  approve: boolean;
  notes?: string;
}

export interface GuardianAccessResult {
  canAccess: boolean;
  guardian?: Partial<Guardian>;
  accessibleFields: string[];
}

export interface UseGuardianReturn {
  /** Guardians for the current user */
  myGuardians: Guardian[];

  /** Users I am guardian for */
  guardedUsers: Guardian[];

  /** Pending enrollments for compliance review */
  pendingEnrollments: Guardian[];

  /** Loading state */
  loading: boolean;

  /** Error message */
  error: string | null;

  /** Enroll a new guardian */
  enrollGuardian: (input: EnrollGuardianInput) => Promise<Guardian | null>;

  /** Submit enrollment for compliance review */
  submitForReview: (guardianId: string, userId: string) => Promise<boolean>;

  /** Verify guardian enrollment (compliance officer) */
  verifyGuardian: (input: VerifyGuardianInput) => Promise<boolean>;

  /** Suspend guardian access */
  suspendGuardian: (guardianId: string, userId: string, reason: string) => Promise<boolean>;

  /** Revoke guardian access */
  revokeGuardian: (guardianId: string, userId: string) => Promise<boolean>;

  /** Check if I can access L1G for a user */
  checkAccess: (userId: string) => Promise<GuardianAccessResult | null>;

  /** Refetch guardian data */
  refetch: () => Promise<void>;

  /** Clear error */
  clearError: () => void;
}

// ============================================================================
// Hook Implementation
// ============================================================================

export function useGuardian(): UseGuardianReturn {
  const { isAuthenticated } = useAuth();
  const [error, setError] = useState<string | null>(null);

  // Query: My guardians
  const {
    data: myGuardiansData,
    loading: myGuardiansLoading,
    refetch: refetchMyGuardians,
  } = useQuery<Record<string, any>>(MY_GUARDIANS, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  // Query: Users I guard
  const {
    data: guardedUsersData,
    loading: guardedUsersLoading,
    refetch: refetchGuardedUsers,
  } = useQuery<Record<string, any>>(GUARDED_USERS, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  // Query: Pending enrollments (for compliance)
  const {
    data: pendingData,
    loading: pendingLoading,
    refetch: refetchPending,
  } = useQuery<Record<string, any>>(PENDING_ENROLLMENTS, {
    skip: !isAuthenticated,
    fetchPolicy: 'cache-and-network',
  });

  // Mutations
  const [enrollMutation, { loading: enrollLoading }] = useMutation<Record<string, any>>(ENROLL_GUARDIAN);
  const [submitMutation, { loading: submitLoading }] = useMutation<Record<string, any>>(SUBMIT_FOR_REVIEW);
  const [verifyMutation, { loading: verifyLoading }] = useMutation<Record<string, any>>(VERIFY_GUARDIAN);
  const [suspendMutation, { loading: suspendLoading }] = useMutation<Record<string, any>>(SUSPEND_GUARDIAN);
  const [revokeMutation, { loading: revokeLoading }] = useMutation<Record<string, any>>(REVOKE_GUARDIAN);

  // Lazy query for access check
  const [checkAccessQuery] = useMutation<Record<string, any>>(CAN_ACCESS_L1G);

  // Combined loading state
  const loading =
    myGuardiansLoading ||
    guardedUsersLoading ||
    pendingLoading ||
    enrollLoading ||
    submitLoading ||
    verifyLoading ||
    suspendLoading ||
    revokeLoading;

  // Enroll guardian
  const enrollGuardian = useCallback(
    async (input: EnrollGuardianInput): Promise<Guardian | null> => {
      setError(null);
      try {
        const { data } = await enrollMutation({
          variables: { input },
        });

        if (data?.enrollGuardian?.error) {
          setError(data.enrollGuardian.error);
          return null;
        }

        await refetchMyGuardians();
        return data?.enrollGuardian?.guardian ?? null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to enroll guardian';
        setError(message);
        return null;
      }
    },
    [enrollMutation, refetchMyGuardians]
  );

  // Submit for review
  const submitForReview = useCallback(
    async (guardianId: string, userId: string): Promise<boolean> => {
      setError(null);
      try {
        const { data } = await submitMutation({
          variables: { guardianId, userId },
        });

        if (data?.submitForReview?.error) {
          setError(data?.submitForReview.error);
          return false;
        }

        await refetchMyGuardians();
        return data?.submitForReview?.success ?? false;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to submit for review';
        setError(message);
        return false;
      }
    },
    [submitMutation, refetchMyGuardians]
  );

  // Verify guardian (compliance)
  const verifyGuardian = useCallback(
    async (input: VerifyGuardianInput): Promise<boolean> => {
      setError(null);
      try {
        const { data } = await verifyMutation({
          variables: { input },
        });

        if (data?.verifyGuardian?.error) {
          setError(data.verifyGuardian.error);
          return false;
        }

        await refetchPending();
        return data?.verifyGuardian?.success ?? false;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to verify guardian';
        setError(message);
        return false;
      }
    },
    [verifyMutation, refetchPending]
  );

  // Suspend guardian
  const suspendGuardian = useCallback(
    async (guardianId: string, userId: string, reason: string): Promise<boolean> => {
      setError(null);
      try {
        const { data } = await suspendMutation({
          variables: { guardianId, userId, reason },
        });

        if (data?.suspendGuardian?.error) {
          setError(data.suspendGuardian.error);
          return false;
        }

        await refetchMyGuardians();
        return data?.suspendGuardian?.success ?? false;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to suspend guardian';
        setError(message);
        return false;
      }
    },
    [suspendMutation, refetchMyGuardians]
  );

  // Revoke guardian
  const revokeGuardian = useCallback(
    async (guardianId: string, userId: string): Promise<boolean> => {
      setError(null);
      try {
        const { data } = await revokeMutation({
          variables: { guardianId, userId },
        });

        await refetchMyGuardians();
        return data?.revokeGuardian ?? false;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to revoke guardian';
        setError(message);
        return false;
      }
    },
    [revokeMutation, refetchMyGuardians]
  );

  // Check access
  const checkAccess = useCallback(
    async (userId: string): Promise<GuardianAccessResult | null> => {
      setError(null);
      try {
        const { data } = await checkAccessQuery({
          variables: { userId },
        });

        return data?.canAccessL1g ?? null;
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to check access';
        setError(message);
        return null;
      }
    },
    [checkAccessQuery]
  );

  // Refetch all
  const refetch = useCallback(async () => {
    await Promise.all([refetchMyGuardians(), refetchGuardedUsers(), refetchPending()]);
  }, [refetchMyGuardians, refetchGuardedUsers, refetchPending]);

  // Clear error
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    myGuardians: myGuardiansData?.myGuardians ?? [],
    guardedUsers: guardedUsersData?.guardedUsers ?? [],
    pendingEnrollments: pendingData?.pendingEnrollments ?? [],
    loading,
    error,
    enrollGuardian,
    submitForReview,
    verifyGuardian,
    suspendGuardian,
    revokeGuardian,
    checkAccess,
    refetch,
    clearError,
  };
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Get human-readable guardian type label
 */
export function getGuardianTypeLabel(type: GuardianType): string {
  switch (type) {
    case GuardianType.HEALTHCARE_PROXY:
      return 'Healthcare Proxy';
    case GuardianType.POWER_OF_ATTORNEY:
      return 'Power of Attorney';
    case GuardianType.COURT_APPOINTED:
      return 'Court-Appointed Guardian';
    case GuardianType.EMERGENCY_CONTACT:
      return 'Emergency Contact';
    default:
      return type;
  }
}

/**
 * Get human-readable guardian status label
 */
export function getGuardianStatusLabel(status: GuardianStatus): string {
  switch (status) {
    case GuardianStatus.PENDING_VERIFICATION:
      return 'Pending Verification';
    case GuardianStatus.UNDER_REVIEW:
      return 'Under Review';
    case GuardianStatus.ACTIVE:
      return 'Active';
    case GuardianStatus.SUSPENDED:
      return 'Suspended';
    case GuardianStatus.REVOKED:
      return 'Revoked';
    case GuardianStatus.EXPIRED:
      return 'Expired';
    default:
      return status;
  }
}

/**
 * Get status badge color for Mantine
 */
export function getGuardianStatusColor(status: GuardianStatus): string {
  switch (status) {
    case GuardianStatus.ACTIVE:
      return 'green';
    case GuardianStatus.PENDING_VERIFICATION:
    case GuardianStatus.UNDER_REVIEW:
      return 'yellow';
    case GuardianStatus.SUSPENDED:
      return 'orange';
    case GuardianStatus.REVOKED:
    case GuardianStatus.EXPIRED:
      return 'red';
    default:
      return 'gray';
  }
}

/**
 * Get guardian type description
 */
export function getGuardianTypeDescription(type: GuardianType): string {
  switch (type) {
    case GuardianType.HEALTHCARE_PROXY:
      return 'Can make medical decisions and access healthcare information (name, DOB, allergies, medications).';
    case GuardianType.POWER_OF_ATTORNEY:
      return 'Limited authority for specific matters. Cannot access financial (L1) data.';
    case GuardianType.COURT_APPOINTED:
      return 'Full legal authority granted by court order. Can access all L1G fields.';
    case GuardianType.EMERGENCY_CONTACT:
      return 'View-only access to emergency information and basic contact details.';
    default:
      return '';
  }
}

/**
 * Get required documents for guardian type
 */
export function getRequiredDocuments(type: GuardianType): string[] {
  switch (type) {
    case GuardianType.HEALTHCARE_PROXY:
      return ['Healthcare Proxy Form', 'Government ID'];
    case GuardianType.POWER_OF_ATTORNEY:
      return ['Power of Attorney Document', 'Government ID'];
    case GuardianType.COURT_APPOINTED:
      return ['Court Order', 'Government ID'];
    case GuardianType.EMERGENCY_CONTACT:
      return []; // Optional documents only
    default:
      return [];
  }
}
